import { useMemo } from "react";
import { Gamepad2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Display the list of animations detected by the LLM. For each animation, if
 * the Sellsy catalog has multiple variants for that base name, render a
 * dropdown so the user picks the right one. Single-variant animations are
 * auto-selected (no dropdown shown).
 *
 * Props:
 *  - selections: array of `{ base_name, sellsy_item_id, reference, variant_label, price }`
 *  - animations: full Sellsy catalog grouped by base name (from /api/sellsy/catalog/animations)
 *  - onChange(idx, nextSelection): user picked a different variant (or auto-select)
 *  - onRemove(idx): user removed an animation
 */
const AnimationVariantPicker = ({ selections, animations, onChange, onRemove }) => {
  // Index catalog by base_name (lowercased) for fast lookup
  // Flat list of every variant across all groups — we now match by SUBSTRING
  // on the full Sellsy reference, not by exact base_name. This way "Caricaturiste"
  // surfaces "Caricaturiste numérique", "YB - Caricaturiste", "YB - Caricaturiste Ipad"
  // etc. all together so the user can pick the right one.
  const allVariants = useMemo(() => {
    const out = [];
    (animations || []).forEach((g) => {
      (g.variants || []).forEach((v) => out.push(v));
    });
    return out;
  }, [animations]);

  if (!selections || selections.length === 0) return null;

  return (
    <div className="space-y-1.5" data-testid="animation-variant-picker">
      {selections.map((sel, idx) => {
        const baseName = sel.base_name;
        const keyword = (baseName || "").toLowerCase().trim();
        // Substring match on the full reference — handles cross-base-name
        // groupings (e.g., "Caricaturiste" surfaces 4 distinct items).
        const variants = keyword
          ? allVariants.filter((v) => (v.reference || "").toLowerCase().includes(keyword))
          : [];
        const hasMultipleVariants = variants.length > 1;
        const inCatalog = variants.length > 0;

        return (
          <div
            key={`${baseName}-${idx}`}
            className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-200"
            data-testid={`animation-row-${idx}`}
          >
            <Gamepad2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p
                className="text-xs font-medium text-slate-900 truncate"
                data-testid={`animation-base-name-${idx}`}
                title={baseName}
              >
                {baseName}
              </p>
              {!inCatalog && (
                <p className="text-[10px] text-amber-600">
                  Non trouvée dans le catalogue Sellsy
                </p>
              )}
              {inCatalog && hasMultipleVariants && (
                <Select
                  value={sel.sellsy_item_id || ""}
                  onValueChange={(value) => {
                    const v = variants.find((x) => x.sellsy_item_id === value);
                    if (!v) return;
                    onChange(idx, {
                      base_name: baseName,
                      sellsy_item_id: v.sellsy_item_id,
                      reference: v.reference,
                      variant_label: v.variant_label,
                      price: v.price,
                    });
                  }}
                >
                  <SelectTrigger
                    className="h-7 text-xs mt-1"
                    data-testid={`animation-variant-trigger-${idx}`}
                  >
                    <SelectValue placeholder="Choisir la caractéristique…" />
                  </SelectTrigger>
                  <SelectContent>
                    {variants.map((v) => (
                      <SelectItem
                        key={v.sellsy_item_id}
                        value={v.sellsy_item_id}
                        data-testid={`animation-variant-option-${idx}-${v.sellsy_item_id}`}
                      >
                        {v.reference}
                        {v.price ? (
                          <span className="text-slate-400 ml-2">
                            · {v.price.toLocaleString("fr-FR")} €
                          </span>
                        ) : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {inCatalog && !hasMultipleVariants && sel.reference && (
                <p className="text-[10px] text-slate-500 truncate">
                  {sel.reference}
                  {sel.price ? ` · ${sel.price.toLocaleString("fr-FR")} €` : ""}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="text-slate-400 hover:text-slate-700 flex-shrink-0"
              title="Retirer cette animation"
              data-testid={`remove-animation-${idx}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AnimationVariantPicker;
